import type { TFunction } from "i18next";
import { NS } from "../../../../U3121";
import type { U3121BillingReadinessHint, U3121BillingSummary } from "../../../../U3121Service";

interface U3121BillingSummaryReadinessBannerProps {
  summary: U3121BillingSummary;
  projectStatus: boolean;
  t: TFunction;
}

function readinessMessageKey(hint: U3121BillingReadinessHint): string {
  switch (hint) {
    case "not_billable":
      return "readinessNotBillable";
    case "blocked_by_timesheets":
      return "readinessBlockedByTimesheets";
    case "has_uninvoiced_wip":
      return "readinessHasUninvoicedWip";
    default:
      return "readinessReady";
  }
}

function readinessBadgeClass(hint: U3121BillingReadinessHint): string {
  switch (hint) {
    case "not_billable":
      return "bg-slate-200 text-slate-800";
    case "blocked_by_timesheets":
      return "bg-amber-100 text-amber-900";
    case "has_uninvoiced_wip":
      return "bg-sky-100 text-sky-900";
    default:
      return "bg-emerald-100 text-emerald-900";
  }
}

function U3121BillingSummaryReadinessBanner({ summary, projectStatus, t }: U3121BillingSummaryReadinessBannerProps) {
  const hint = summary.readinessHint;
  const showProjectFinished = projectStatus === false && hint === "ready";
  const displayKey = showProjectFinished ? "readinessProjectFinished" : readinessMessageKey(hint);
  const badgeClass = showProjectFinished ? "bg-slate-200 text-slate-800" : readinessBadgeClass(hint);

  return (
    <div className="rounded-lg border border-gray-200 bg-muted/40 p-4 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{t("sectionBillingSummaryReadiness", { ns: NS })}</span>
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClass}`}>
          {t(displayKey, { ns: NS })}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {t("lblBillingSummaryScope", {
          ns: NS,
          billable: summary.billable ? t("lblBillableYes", { ns: NS }) : t("lblBillableNo", { ns: NS }),
          includeSub: summary.includeSubProjet ? t("lblIncludeSubProjectYes", { ns: NS }) : t("lblIncludeSubProjectNo", { ns: NS }),
        })}
      </p>
    </div>
  );
}

export default U3121BillingSummaryReadinessBanner;
